import React, { useCallback, useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { CheckCircle2, Circle, Clock } from 'lucide-react';
import { describeApiError, getOrderByEmail } from '../services/api';
import { carrierLabel, trackingUrl } from '../utils/carriers';
import { formatCents } from '../utils/money';
import { statusLabel, stepReached } from '../utils/orderProgress';
import type { Order, OrderStatus as Status } from '../types';

const STATUS_LABEL: Record<Status, string> = {
  created: 'Received',
  invoice_sent: 'Awaiting payment',
  paid: 'Paid',
  in_progress: 'Being made',
  ready_for_pickup: 'Ready for pickup',
  shipped: 'Shipped',
  completed: 'Complete',
  cancelled: 'Cancelled',
  refunded: 'Refunded',
};

const DELIVERY_LABELS: Record<Exclude<Order['fulfillment'], 'none'>, string> = {
  pickup: 'Local pickup',
  dropoff: 'Local drop-off',
  shipping: 'Shipping',
};

// The normal happy path, in order. Cancelled/refunded are side branches and
// deliberately absent — they render as a plain notice instead.
const TIMELINE: Status[] = ['created', 'invoice_sent', 'paid', 'in_progress'];

const longDate = (iso?: string): string => {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
};

const OrderStatusPage: React.FC = () => {
  // Emails link here with the lookup already answered, since both fields
  // are in the message the customer is reading — see _status_url in the
  // backend. Typed in by hand otherwise.
  const [params] = useSearchParams();
  const [email, setEmail] = useState(params.get('email') ?? '');
  const [orderNumber, setOrderNumber] = useState(params.get('order') ?? '');
  const [order, setOrder] = useState<Order | null>(null);
  const [error, setError] = useState<string | undefined>();
  const [loading, setLoading] = useState(false);

  const lookUp = useCallback(async (emailValue: string, numberValue: string) => {
    setLoading(true);
    setError(undefined);
    setOrder(null);
    try {
      setOrder(await getOrderByEmail(emailValue.trim(), numberValue.trim()));
    } catch (e: any) {
      setError(
        e.response?.status === 404
          ? "We couldn't find that order. Check the number and the email you used."
          : describeApiError(e, 'Lookup failed.')
      );
    } finally {
      setLoading(false);
    }
  }, []);

  // Arriving from an email shows the order straight away rather than a
  // filled-in form the customer still has to submit.
  useEffect(() => {
    const linkedEmail = params.get('email');
    const linkedNumber = params.get('order');
    if (linkedEmail && linkedNumber) lookUp(linkedEmail, linkedNumber);
    // Runs for the link the page was opened with; typing afterwards uses
    // the form.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    lookUp(email, orderNumber);
  };

  const isPaid = Boolean(order?.paid_at);
  const finished = order && ['cancelled', 'refunded'].includes(order.status);

  return (
    <div className="max-w-2xl mx-auto px-4 sm:px-6 py-10">
      <h1 className="text-4xl mb-4">Track your order</h1>
      <p className="text-sm text-text-light mb-4">
        Enter the email and order number from your confirmation email.
      </p>

      <form onSubmit={onSubmit} className="space-y-3">
        <input
          type="email"
          required
          placeholder="Email"
          value={email}
          onChange={e => setEmail(e.target.value)}
          className="w-full border border-background-dark rounded px-3 py-2 text-sm"
        />
        <input
          type="text"
          required
          placeholder="Order number (e.g. BLM-20260808-A1B2)"
          value={orderNumber}
          onChange={e => setOrderNumber(e.target.value)}
          className="w-full border border-background-dark rounded px-3 py-2 text-sm"
        />
        <button
          type="submit"
          disabled={loading}
          className="bg-primary text-white rounded px-4 py-2 text-sm font-medium disabled:opacity-50"
        >
          {loading ? 'Looking up…' : 'Look up order'}
        </button>
      </form>

      {error && <div className="mt-4 text-sm text-red-600">{error}</div>}

      {order && (
        <div className="mt-8 space-y-5">
          {/* Headline: status and whether money is still owed. */}
          <div className="bg-white border border-background-dark rounded p-5">
            <div className="flex items-start justify-between gap-4 flex-wrap">
              <div>
                <div className="text-xs uppercase tracking-wide text-text-light">Status</div>
                <div className="text-2xl">{statusLabel(STATUS_LABEL, order.status, order.fulfillment)}</div>
                <div className="text-xs text-text-light font-mono mt-1">{order.order_number}</div>
              </div>
              <div className="text-right">
                <div className="text-xs uppercase tracking-wide text-text-light">
                  {isPaid ? 'Total paid' : 'Amount due'}
                </div>
                <div className="text-2xl">{formatCents(order.total_cents)}</div>
                {isPaid ? (
                  <span className="inline-flex items-center gap-1 text-xs bg-green-50 text-green-900 border border-green-200 rounded px-2 py-0.5 mt-1">
                    <CheckCircle2 size={12} /> Paid
                    {order.payment_method ? ` · ${order.payment_method}` : ''}
                  </span>
                ) : (
                  <span className="inline-flex items-center gap-1 text-xs bg-amber-50 text-amber-900 border border-amber-200 rounded px-2 py-0.5 mt-1">
                    <Clock size={12} /> Not yet paid
                  </span>
                )}
              </div>
            </div>
          </div>

          {finished ? (
            <div className="bg-white border border-background-dark rounded p-5 text-sm text-text-light">
              This order was {order.status}. Get in touch if that's unexpected.
            </div>
          ) : (
            <div className="bg-white border border-background-dark rounded p-5">
              <div className="text-xs uppercase tracking-wide text-text-light mb-3">Progress</div>
              <ol className="space-y-2">
                {TIMELINE.map(step => {
                  const done = stepReached(order, step);
                  // Where the order sits right now, which needn't be the
                  // last step that's been ticked.
                  const current = step === order.status;
                  return (
                    <li key={step} className="flex items-center gap-2 text-sm">
                      {done ? (
                        <CheckCircle2
                          size={16}
                          className={current ? 'text-primary' : 'text-green-600'}
                        />
                      ) : (
                        <Circle
                          size={16}
                          className={current ? 'text-primary' : 'text-background-dark'}
                        />
                      )}
                      <span className={current ? 'font-medium' : done ? '' : 'text-text-light'}>
                        {statusLabel(STATUS_LABEL, step, order.fulfillment)}
                        {current && <span className="text-text-light font-normal"> — now</span>}
                      </span>
                    </li>
                  );
                })}
              </ol>
            </div>
          )}

          {/* Dates and delivery detail. */}
          <div className="bg-white border border-background-dark rounded p-5 text-sm space-y-2">
            <div className="flex justify-between gap-4">
              <span className="text-text-light">Ordered</span>
              <span>{longDate(order.created_at)}</span>
            </div>
            {order.paid_at && (
              <div className="flex justify-between gap-4">
                <span className="text-text-light">Paid</span>
                <span>{longDate(order.paid_at)}</span>
              </div>
            )}
            {order.due_date && !order.completed_at && (
              <div className="flex justify-between gap-4">
                <span className="text-text-light">Expected ready</span>
                <span>{longDate(order.due_date)}</span>
              </div>
            )}
            {order.completed_at && (
              <div className="flex justify-between gap-4">
                <span className="text-text-light">Completed</span>
                <span>{longDate(order.completed_at)}</span>
              </div>
            )}
            {order.fulfillment !== 'none' && (
              <div className="flex justify-between gap-4">
                <span className="text-text-light">Delivery</span>
                <span>{DELIVERY_LABELS[order.fulfillment]}</span>
              </div>
            )}
            {order.tracking_number && (
              <div className="flex justify-between gap-4">
                <span className="text-text-light">Tracking</span>
                {/* Linked straight to the carrier where we know their
                    tracking page — copying a number into the right site is
                    the fiddly part. Plain text otherwise, since a guessed
                    URL that 404s is worse than none. */}
                {trackingUrl(order.carrier, order.tracking_number) ? (
                  <a
                    href={trackingUrl(order.carrier, order.tracking_number)}
                    target="_blank"
                    rel="noreferrer"
                    className="text-primary hover:underline text-right"
                  >
                    {carrierLabel(order.carrier)}{' '}
                    <span className="font-mono text-xs">{order.tracking_number}</span>
                  </a>
                ) : (
                  <span className="font-mono text-xs">
                    {order.carrier ? `${carrierLabel(order.carrier)} · ` : ''}
                    {order.tracking_number}
                  </span>
                )}
              </div>
            )}
          </div>

          {/* What was ordered. */}
          {order.lines.length > 0 && (
            <div className="bg-white border border-background-dark rounded p-5">
              <div className="text-xs uppercase tracking-wide text-text-light mb-3">Items</div>
              <ul className="space-y-2 text-sm">
                {order.lines.map((l, i) => (
                  <li key={i} className="flex justify-between gap-4">
                    <span>
                      {l.qty} × {l.product_name}
                      {l.description && (
                        <span className="block text-xs text-text-light">{l.description}</span>
                      )}
                    </span>
                    <span className="whitespace-nowrap">{formatCents(l.line_total_cents)}</span>
                  </li>
                ))}
              </ul>
              <div className="border-t border-background-dark mt-3 pt-3 text-sm space-y-1">
                <div className="flex justify-between">
                  <span className="text-text-light">Subtotal</span>
                  <span>{formatCents(order.subtotal_cents)}</span>
                </div>
                {order.shipping_cents > 0 && (
                  <div className="flex justify-between">
                    <span className="text-text-light">Shipping</span>
                    <span>{formatCents(order.shipping_cents)}</span>
                  </div>
                )}
                {order.tax_cents > 0 && (
                  <div className="flex justify-between">
                    <span className="text-text-light">
                      Tax{order.tax_rate_percent ? ` (${order.tax_rate_percent}%)` : ''}
                    </span>
                    <span>{formatCents(order.tax_cents)}</span>
                  </div>
                )}
                <div className="flex justify-between font-medium pt-1 border-t border-background-dark">
                  <span>{isPaid ? 'Paid' : 'Amount due'}</span>
                  <span>{formatCents(order.total_cents)}</span>
                </div>
              </div>
            </div>
          )}

          {order.invoice_token && (
            <a
              href={`/invoice/${order.invoice_token}`}
              className="inline-block text-sm text-primary hover:underline"
            >
              View full invoice →
            </a>
          )}
        </div>
      )}
    </div>
  );
};

export default OrderStatusPage;
